import React from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { selectAddHeightDataPending } from '../../../../redux/profiles/patientHealthboard/selectors';
import * as addHeightData from '../../../../redux/profiles/patientHealthboard/add-height-data';
import { useFormState } from '../../../../hocs/useFormWithValidation';
import { COLORS } from '../../../../constants/styles';
import { IDS } from '../../../../constants/ids';
import DatePickerCustom from '../../../common/DatePickerCustom';
import TimePickerInput from '../../../common/TimePickerInput';
import Loader from '../../../common/Loader';
import TextInput from '../../../TextInput';
import Button from '../../../Button';
import Icon from '../../../Icon';
import {
  convertValueToMeters,
  getCurrentTime,
  validateFutureTime,
} from '../../utils';
import styles from '../index.styl';

const {
  PATIENT_PORTAL: { ADD_HEIGHT_FORM },
} = IDS;

export const AddHeightForm = ({
  formatMessage,
  modalMode,
  handleRedirect,
  isHeightInMeters,
}) => {
  const currentTime = moment().format('h:mm A');
  const { formState, handleFormChange } = useFormState({
    [ADD_HEIGHT_FORM.TIME]: currentTime,
  });
  const currentDate = new Date();

  const dispatch = useDispatch();
  const pending = useSelector(selectAddHeightDataPending);
  const combinedDisable =
    !formState[ADD_HEIGHT_FORM.FEET] ||
    (!isHeightInMeters && !formState[ADD_HEIGHT_FORM.INCH]) ||
    !formState[ADD_HEIGHT_FORM.TIME] ||
    pending;

  const handleSubmit = e => {
    e.preventDefault();
    const safeDate = moment(formState[ADD_HEIGHT_FORM.DATE]).format(
      'YYYY-MM-D',
    );
    const safeTime = formState[ADD_HEIGHT_FORM.TIME]
      ? `T${formState[ADD_HEIGHT_FORM.TIME]}`
      : `T${moment().format('hh:mm A')}`;

    const heightValue = isHeightInMeters
      ? parseInt(formState[ADD_HEIGHT_FORM.FEET], 10) / 100
      : convertValueToMeters(
          formState[ADD_HEIGHT_FORM.FEET],
          formState[ADD_HEIGHT_FORM.INCH],
        );

    dispatch(
      addHeightData.actions.send({
        local_height_measurement: {
          measured_at: moment(
            safeDate + safeTime,
            'YYYY-MM-DThh:mm A',
          ).format(),
          value: { baseValue: heightValue },
        },
      }),
    );

    handleFormChange({
      [ADD_HEIGHT_FORM.FEET]: '',
      [ADD_HEIGHT_FORM.INCH]: '',
    });

    if (handleRedirect) {
      handleRedirect();
    }
  };

  return (
    <div className={cn(styles.root, { [styles.rootModalMode]: modalMode })}>
      {!modalMode && (
        <div className={styles.title}>
          {formatMessage(
            { id: 'patient.portal.emptyState.addData' },
            { type: formatMessage({ id: 'patient.healthboard.height' }) },
          )}
        </div>
      )}
      {pending && <Loader className={cn(styles.loader, styles.loaderHeight)} />}
      <form
        className={cn(styles.content, {
          [styles.modalMode]: modalMode,
        })}
        onSubmit={handleSubmit}
      >
        <div className={styles.dateWrapper}>
          <div className={styles.datePickerWrapper}>
            <DatePickerCustom
              value={formState[ADD_HEIGHT_FORM.DATE] || currentDate}
              label={formatMessage({ id: 'common.date' })}
              name={ADD_HEIGHT_FORM.DATE}
              maxDate={new Date()}
              formatDate="MMM D, YYYY"
              onChange={value =>
                handleFormChange({
                  [ADD_HEIGHT_FORM.DATE]: value,
                  [ADD_HEIGHT_FORM.TIME]: '',
                })
              }
              disabled={pending}
            />
            <Icon
              name="calendar"
              style={{ fill: COLORS.bluePrimary }}
              className={styles.datePickerIcon}
            />
          </div>
          <TimePickerInput
            value={formState[ADD_HEIGHT_FORM.TIME]}
            name={ADD_HEIGHT_FORM.TIME}
            onChange={value =>
              handleFormChange({ [ADD_HEIGHT_FORM.TIME]: value.toUpperCase() })
            }
            label={formatMessage({ id: 'reports.time' })}
            placeholder={formatMessage({ id: 'reports.time' })}
            className={styles.timePickerWrapper}
            disabled={pending}
            validateTime={pickTime =>
              validateFutureTime(
                formatMessage,
                pickTime,
                formState[ADD_HEIGHT_FORM.DATE],
              )
            }
            time={getCurrentTime()}
          />
        </div>
        <div className={styles.heightInputWrapper}>
          <TextInput
            id={ADD_HEIGHT_FORM.FEET}
            type="number"
            label={formatMessage({ id: 'patient.healthboard.height' })}
            limit={isHeightInMeters ? 3 : 1}
            isFractionNumber
            placeholder={formatMessage({
              id: `profile.section.unitsSettings.height.metric.${
                isHeightInMeters ? 'meters' : 'feet_and_inches'
              }`,
            })}
            value={formState[ADD_HEIGHT_FORM.FEET]}
            onChange={value =>
              handleFormChange({ [ADD_HEIGHT_FORM.FEET]: value })
            }
            className={cn(styles.weightInput, styles.marginFirstChild)}
            disabled={pending}
            min={1}
          />
          {!isHeightInMeters && (
            <TextInput
              limit={2}
              type="number"
              isFractionNumber
              disabled={pending}
              id={ADD_HEIGHT_FORM.INCH}
              className={styles.weightInput}
              value={formState[ADD_HEIGHT_FORM.INCH]}
              placeholder={formatMessage({
                id: 'patient.healthboard.height.metric.inches',
              })}
              onChange={value =>
                handleFormChange({ [ADD_HEIGHT_FORM.INCH]: value })
              }
            />
          )}
        </div>
        <Button
          id={ADD_HEIGHT_FORM.ADD_BUTTON}
          raised
          color="primary"
          className={styles.addButton}
          disabled={combinedDisable}
        >
          {formatMessage({ id: 'common.add' })}
        </Button>
      </form>
    </div>
  );
};

AddHeightForm.propTypes = {
  formatMessage: PropTypes.func,
  modalMode: PropTypes.bool,
  handleRedirect: PropTypes.func,
  isHeightInMeters: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Forms/AddHeightForm/index.jsx